import fetchApi from '@/lib/tools/api'
import FeedbackIcon from '@mui/icons-material/Feedback'
import {Box, Typography} from '@mui/material'
import {CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip} from 'chart.js'
import {Dayjs} from 'dayjs'
import React, {useEffect, useState} from 'react'
import {Line} from 'react-chartjs-2'
import CardWithIcon from './CardWithIcon'
import PeriodSelector from './PeriodSelector'

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
)

interface ContributionStats {
	data: {
		period: string
		terroirs: number
		app: number
		total: number
	}[]
	period_months?: number
	period_type: 'days' | 'months'
	period_value: number
}

const ContributionsChartJS = () => {
	const [stats, setStats] = useState<ContributionStats | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [selectedPeriod, setSelectedPeriod] = useState<'30d' | '90d' | '12m' | '24m' | 'custom'>('30d')
	const [startDate, setStartDate] = useState<Dayjs>()
	const [endDate, setEndDate] = useState<Dayjs>()

	const fetchStats = async (period: '30d' | '90d' | '12m' | '24m' | 'custom', customStartDate?: Dayjs, customEndDate?: Dayjs) => {
		try {
			setLoading(true)
			let url = '/stats/contributions'

			if (period === 'custom' && customStartDate && customEndDate) {
				url += `?start_date=${customStartDate.format('YYYY-MM-DD')}&end_date=${customEndDate.format('YYYY-MM-DD')}`
			} else {
				const days = period === '30d' ? 30 : period === '90d' ? 90 : period === '12m' ? 365 : 730 // 24m = 730 days
				url += `?days=${days}`
			}

			console.log('🔄 ContributionsChartJS: Fetching stats...', url)
			const result = await fetchApi<ContributionStats>(url)
			console.log('✅ ContributionsChartJS: Data received:', result)
			setStats(result)
			setError(null)
		} catch (err) {
			console.error('❌ ContributionsChartJS: Error fetching stats:', err)
			setError('Erreur lors du chargement des statistiques')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchStats(selectedPeriod, startDate, endDate)
	}, [])

	const handlePeriodChange = (period: '30d' | '90d' | '12m' | '24m' | 'custom', customStartDate?: Dayjs, customEndDate?: Dayjs) => {
		setSelectedPeriod(period)
		setStartDate(customStartDate)
		setEndDate(customEndDate)
		fetchStats(period, customStartDate, customEndDate)
	}

	if (loading) {
		return (
			<CardWithIcon
				icon={FeedbackIcon}
				title={'Contributions'}
				subtitle="Chargement..."
			>
				<Box sx={{
					height: '180px',
					padding: '20px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}>
					<Typography variant="body2">Chargement des statistiques...</Typography>
				</Box>
			</CardWithIcon>
		)
	}

	if (error) {
		return (
			<CardWithIcon
				icon={FeedbackIcon}
				title={'Contributions'}
				subtitle="Erreur"
			>
				<Box sx={{
					height: '180px',
					padding: '20px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}>
					<Typography variant="body2" color="error">{error}</Typography>
				</Box>
			</CardWithIcon>
		)
	}

	const totalContributions = stats?.data.reduce((sum, item) => sum + item.total, 0) || 0

	const chartData = {
		labels: stats?.data.map(d => d.period) || [],
		datasets: [
			{
				label: 'Terroirs',
				data: stats?.data.map(d => d.terroirs) || [],
				borderColor: '#10b981',
				backgroundColor: (context: any) => {
					const ctx = context.chart.ctx
					const gradient = ctx.createLinearGradient(0, 0, 0, 200)
					gradient.addColorStop(0, 'rgba(16, 185, 129, 0.2)')
					gradient.addColorStop(1, 'rgba(16, 185, 129, 0.02)')
					return gradient
				},
				borderWidth: 2.5,
				tension: 0.4,
				pointRadius: 3,
				pointHoverRadius: 6,
				pointBackgroundColor: '#10b981',
				pointBorderColor: '#ffffff',
				pointBorderWidth: 2,
				fill: true
			},
			{
				label: 'App',
				data: stats?.data.map(d => d.app) || [],
				borderColor: '#3b82f6',
				backgroundColor: (context: any) => {
					const ctx = context.chart.ctx
					const gradient = ctx.createLinearGradient(0, 0, 0, 200)
					gradient.addColorStop(0, 'rgba(59, 130, 246, 0.2)')
					gradient.addColorStop(1, 'rgba(59, 130, 246, 0.02)')
					return gradient
				},
				borderWidth: 2.5,
				tension: 0.4,
				pointRadius: 3,
				pointHoverRadius: 6,
				pointBackgroundColor: '#3b82f6',
				pointBorderColor: '#ffffff',
				pointBorderWidth: 2,
				fill: true
			}
		]
	}

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		interaction: {
			intersect: false,
			mode: 'index' as const
		},
		plugins: {
			legend: {
				position: 'bottom' as const,
				labels: {
					font: {
						size: 11,
						family: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
						weight: 500
					},
					color: '#64748b',
					padding: 16,
					usePointStyle: true,
					pointStyle: 'circle'
				}
			},
			tooltip: {
				backgroundColor: 'rgba(255, 255, 255, 0.95)',
				titleColor: '#1e293b',
				bodyColor: '#475569',
				borderColor: '#e2e8f0',
				borderWidth: 1,
				cornerRadius: 8,
				padding: 12,
				boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
			}
		},
		scales: {
			x: {
				grid: {
					display: false
				},
				border: {
					display: false
				},
				ticks: {
					font: {
						size: 10,
						family: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
					},
					color: '#94a3b8',
					maxRotation: 0,
					padding: 8
				}
			},
			y: {
				grid: {
					color: 'rgba(148, 163, 184, 0.1)',
					drawBorder: false
				},
				border: {
					display: false
				},
				ticks: {
					font: {
						size: 10,
						family: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
					},
					color: '#94a3b8',
					padding: 8
				},
				beginAtZero: true
			}
		},
		elements: {
			line: {
				borderCapStyle: 'round' as const,
				borderJoinStyle: 'round' as const
			}
		}
	}

	console.log('🎨 ContributionsChartJS: Rendering chart with data:', chartData)

	return (
		<CardWithIcon
			icon={FeedbackIcon}
			title={'Contributions'}
			subtitle={`${totalContributions}`}
		>
			<Box sx={{padding: '20px'}}>
				<Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px'}}>
					<Typography variant="caption">
						Évolution {stats?.period_value} derniers {stats?.period_type === 'days' ? 'jours' : 'mois'}
					</Typography>
					<PeriodSelector
						selectedPeriod={selectedPeriod}
						startDate={startDate}
						endDate={endDate}
						onPeriodChange={handlePeriodChange}
					/>
				</Box>
				<div style={{width: '100%', minHeight: '300px'}}>
					<Line data={chartData} options={options}/>
				</div>
			</Box>
		</CardWithIcon>
	)
}

export default ContributionsChartJS
